import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import LayerDriftAcceptButton from "@/v2-backoffice-client/layer/drift/accept/LayerDriftAcceptButton"
import { pluralChanges } from "@/v2-backoffice-client/layer/drift/layer.drift.utils"
import { countUnresolvedBreaking } from "@/v2-backoffice-client/layer/drift/resolve/layer.drift.resolve"
import { formatRelativeTime } from "@/v2-backoffice-client/uptime/uptime.utils"
import type { LayerDriftFragment } from "@/v2-backoffice-client/layer/drift/__types__/layer.drift.fragment"

export type LayerDriftHeaderProps = ComponentProps<"header"> & {
  detail: LayerDriftFragment
  onAccepted?(): void
}

// LayerDriftHeader is the top of the Drift tab: it tells the operator
// at a glance whether anything is drifting, when the baseline was
// captured / when the current fingerprint was last seen, and (when
// drifted) shows the Accept-baseline action to the right.
//
// No severity-count pills here, unlike the summary surfaces: the list
// directly below opens with "breaking · 3 changes", so a pill row in
// the header states the same three numbers a line earlier.
function LayerDriftHeader(props: LayerDriftHeaderProps) {
  const { detail, onAccepted, className, ...headerProps } = props

  return (
    <header
      className={cn(
        "flex flex-wrap items-start justify-between gap-3",
        className,
      )}
      {...headerProps}
    >
      <div className="min-w-0 space-y-1">
        <div className="flex items-center gap-2 text-sm">
          <Icon
            src="swap"
            className={cn(
              detail.HasDrift ? "text-violet-500" : "text-muted-foreground",
            )}
          />
          {detail.HasDrift ? (
            <span className="font-medium">
              Configuration drift detected (
              {pluralChanges(detail.Entries.length)})
            </span>
          ) : (
            <span className="font-medium text-muted-foreground">No drift</span>
          )}
        </div>

        <div className="text-xs text-muted-foreground space-x-3">
          <span>
            Baseline:{" "}
            {detail.BaselineFirstAt
              ? formatRelativeTime(detail.BaselineFirstAt)
              : detail.BaselineAt
                ? formatRelativeTime(detail.BaselineAt)
                : "—"}
          </span>
          {detail.HasDrift && detail.CurrentFirstAt && (
            <span className="text-violet-500 font-medium">
              Drifted: {formatRelativeTime(detail.CurrentFirstAt)}
            </span>
          )}
          <span>
            Last seen:{" "}
            {detail.CurrentAt ? formatRelativeTime(detail.CurrentAt) : "—"}
          </span>
          <span>
            Protocol: <code className="font-mono">{detail.Protocol}</code>
          </span>
        </div>
      </div>

      {detail.HasDrift && (
        <LayerDriftAcceptButton
          source={detail.Source}
          identifier={detail.Identifier}
          unresolvedCount={countUnresolvedBreaking(detail.Entries)}
          onCompleted={onAccepted}
        />
      )}
    </header>
  )
}

export default LayerDriftHeader
